import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import Check from '../../icons/Check';
import Copy from '../../icons/Copy';
import type { WDSComponentProps } from '../../types';
import { useTrace } from '../../utils/trace';
import { HighlightText } from '../HighlightText/HighlightText';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';

export interface CopiableTextProps extends WDSComponentProps {
    /** The text string to copy to the clipboard. Also used as the display text when `children` is not provided. */
    text: string;
    /** When provided, the displayed text is rendered via HighlightText with matching substrings highlighted. Has no effect when `children` is provided. */
    searchTerm?: string;
    /** When true, text is truncated with an ellipsis on overflow, with a tooltip showing the full text. Has no effect when `children` is provided. @default true */
    noWrap?: boolean;
    /** Custom rendered content. When provided, overrides the default text display. The `text` prop is still used for the clipboard value. */
    children?: ReactNode;
    /** When true, disables the copy interaction. */
    disabled?: boolean;
}

const iconSx: SxProps<Theme> = {
    position: 'absolute',
    right: 0,
    top: '50%',
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(110%, -50%)',
    transition: 'transform 0.2s ease-in-out, opacity 0.2s ease-in-out',
    opacity: 0,
};

const baseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    textAlign: 'inherit',
    position: 'relative',
    overflow: 'hidden',
    transition: 'padding-right 0.2s ease-in-out',
    '& .copiable-text-icon': iconSx,
};

const idleSx: SxProps<Theme> = {
    ...baseSx,
    cursor: 'pointer',
    '&:hover, &:focus-visible': { pr: '24px' },
    '&:hover .copiable-text-icon, &:focus-visible .copiable-text-icon': {
        transform: 'translate(0%, -50%)',
        opacity: 1,
    },
};

const copiedSx: SxProps<Theme> = {
    ...baseSx,
    cursor: 'pointer',
    pr: '24px',
    '& .copiable-text-icon': {
        ...iconSx,
        transform: 'translate(0%, -50%)',
        opacity: 1,
    },
};

const disabledSx: SxProps<Theme> = {
    ...baseSx,
    cursor: 'default',
};

/**
 * Renders text (or custom children) with a copy-to-clipboard action that appears on hover.
 *
 * When `searchTerm` is provided, matching substrings are highlighted via {@link HighlightText}.
 * Clicking copies the `text` prop value to the clipboard and shows a check icon for 2 seconds.
 *
 * [Documentation](https://wds2docs.dev-sqnt.com/?path=/docs/components-copiabletext)
 *
 * ### Example
 * ```tsx
 * <CopiableText text="abc-123" searchTerm="123" automation-id="my-copy" />
 * ```
 */
export function CopiableText({
    text,
    searchTerm,
    noWrap = true,
    children,
    disabled = false,
    'automation-id': automationId,
    sx,
}: Readonly<CopiableTextProps>) {
    const getFullTrace = useTrace(automationId ?? 'copiable-text');
    const [isCopied, setIsCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const clipboardAvailable = typeof navigator !== 'undefined' && !!navigator.clipboard;

    const buttonSx = useMemo(() => {
        if (!clipboardAvailable || disabled) return disabledSx;
        return isCopied ? copiedSx : idleSx;
    }, [clipboardAvailable, disabled, isCopied]);

    useEffect(
        () => () => {
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current);
            }
        },
        [],
    );

    const handleCopy = async () => {
        if (!clipboardAvailable || disabled) return;
        try {
            await navigator.clipboard.writeText(text);
            setIsCopied(true);
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current);
            }
            copiedTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
        } catch {
            // Clipboard write failed
        }
    };

    const contentText = children ?? (
        <Typography component="span" variant="body2">
            {searchTerm ? <HighlightText text={text} searchTerm={searchTerm} /> : text}
        </Typography>
    );

    const content =
        noWrap && children === undefined ? (
            <OverflowTooltip title={text}>{contentText}</OverflowTooltip>
        ) : (
            contentText
        );

    return (
        <ButtonBase
            onClick={handleCopy}
            disabled={!clipboardAvailable || disabled}
            aria-label={`Copy ${text}`}
            sx={[buttonSx, sx] as SxProps<Theme>}
            automation-id={getFullTrace()}
        >
            <Box className="copiable-text-icon" automation-id={getFullTrace('icon')}>
                {isCopied ? (
                    <Check fontSize="small" color="success" />
                ) : (
                    <Copy fontSize="small" color="action" />
                )}
            </Box>
            {content}
        </ButtonBase>
    );
}
